import { SvgIconTypeMap, ToggleButton, ToggleButtonGroup, Tooltip, Typography, darken, styled } from '@mui/material'
import { OverridableComponent } from '@mui/material/OverridableComponent'
import React from 'react'

interface IToggleGroupButtonsProps {
  actions: IToggleGroupButtonActions[]
  defaultValue: any
  value: any
  setViewMode: (value: any) => void
  height?: string
  style?: React.CSSProperties | undefined
  buttonWidth?: string
  fullWidth?: boolean
  fitContent?: boolean
  disabled?: boolean
}

interface IToggleGroupButtonActions {
  title: string
  Icon?: OverridableComponent<SvgIconTypeMap<any, 'svg'>> | React.FunctionComponent<React.SVGProps<SVGSVGElement>>
  value: any
}

const ToggleGroupButtons: React.FC<IToggleGroupButtonsProps> = ({
  actions,
  value,
  setViewMode,
  height,
  style,
  buttonWidth,
  fullWidth,
  fitContent,
  disabled,
}) => {
  return (
    <StyledToggleButtonGroup exclusive value={value} fullWidth={fullWidth} disabled={disabled}>
      {actions.map(action => (
        <Tooltip title={action.title} key={action.value}>
          <ToggleButtonAction
            value={action.value}
            onClick={() => setViewMode(action.value)}
            enable={value == action.value}
            withIcon={action.Icon != null}
            height={height}
            style={style}
            buttonWidth={buttonWidth}
            fitContent={fitContent}
          >
            {action.Icon ? (
              <action.Icon />
            ) : (
              <Typography variant="subtitle2" textTransform={'none'} fontWeight={500}>
                {action.title}
              </Typography>
            )}
          </ToggleButtonAction>
        </Tooltip>
      ))}
    </StyledToggleButtonGroup>
  )
}

export default ToggleGroupButtons

//styles
const ToggleButtonAction = styled(ToggleButton) <{
  enable: boolean
  withIcon: boolean
  height: string | undefined
  buttonWidth: string | undefined
  fitContent?: boolean
}>`
  height: ${props => (props.height ? props.height : '36px')};
  width: ${props => (props.withIcon ? '60px' : props.fitContent ? 'auto' : props.buttonWidth ?? '100%')};
  padding: ${props => (props.withIcon ? '0px' : '0px 16px')};
  background-color: ${props => props.theme.palette.white[props.theme.palette.mode]};
  color: ${props =>
    props.enable
      ? props.theme.palette.white[props.theme.palette.mode]
      : props.theme.palette.black[props.theme.palette.mode]};
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  border-radius: 4px;

  &.Mui-selected,
  &.Mui-selected:hover {
    background-color: ${props => props.theme.palette.white[props.theme.palette.mode]};
    color: ${props => props.theme.palette.white[props.theme.palette.mode]};
  }

  :hover {
    background-color: ${props => props.theme.palette.white[props.theme.palette.mode]};
  }

  &.Mui-selected,
  &.Mui-selected:hover {
    background-color: ${props => props.theme.palette.white[props.theme.palette.mode]};
    color: ${props =>
      props.enable
        ? props.theme.palette.white[props.theme.palette.mode]
        : props.theme.palette.black[props.theme.palette.mode]};
  }

  &:before {
    content: '';
    position: absolute;
    top: 4px;
    left: 4px;
    right: 4px;
    bottom: 4px;
    border-radius: 4px;
    background-color: ${props =>
    props.enable ? props.theme.palette.primaryMain[props.theme.palette.mode] : 'transparent'};
    z-index: 0;
  }

  &:hover:before {
    background-color: ${props =>
    props.enable ? darken(props.theme.palette.primaryMain[props.theme.palette.mode] as string, 0.2) : 'transparent'};
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  '.MuiToggleButtonGroup-grouped:not(:first-of-type) ': { borderLeft: 'none !important' },
}))
